/* eslint-disable jsx-a11y/heading-has-content */
import { Trans, useTranslation } from '@e-kyc/translations';
import { useOs } from '@shared/hooks';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';

import {
  ArOnYouIcon,
  Button,
  DevicesOutlinedIcon,
  EventAvailableOutlinedIcon,
  IDCardIcon,
} from '../../components';
import { useEkyc } from '../../providers';

export default function OnBoardingPage() {
  const os = useOs();
  const isMobile = os === 'android' || os === 'ios';

  if ((os === 'macos' && 'ontouchend' in document) || isMobile) return <OnBoardingMobile />;
  return <OnBoardingDesktop />;
}

function OnBoardingDesktop() {
  const [qrCodeURL, setQrCodeURL] = useState<string>('');
  const { t } = useTranslation();
  useEffect(() => {
    const url = window.location.href;
    QRCode.toDataURL(url, {
      width: 160,
      margin: 0,
    }).then(setQrCodeURL);
  }, []);

  return (
    <div className='flex h-dvh items-center justify-center bg-gray-100'>
      <div className='flex h-[480px] w-[960px] justify-between gap-6 rounded-2xl bg-white p-16'>
        <div className='flex flex-col items-start gap-8'>
          <img src='/logo.svg' alt='logo' className='h-10' />
          <div className='flex flex-col gap-2'>
            <h2>{t('onboarding.desktop.title')}</h2>
            <p className='text-text-secondary'>{t('onboarding.desktop.description')}</p>
          </div>
          <div className='flex flex-col gap-3'>
            <div className='flex gap-3'>
              <DevicesOutlinedIcon className='text-primary' />
              <p>{t('onboarding.method.1')}</p>
            </div>
            <div className='flex gap-3'>
              <IDCardIcon className='text-primary' />
              <p>{t('onboarding.method.2')}</p>
            </div>
            <div className='flex gap-3'>
              <EventAvailableOutlinedIcon className='text-primary' />
              <p>{t('onboarding.method.3')}</p>
            </div>
            <div className='flex gap-3'>
              <div>
                <ArOnYouIcon className='text-primary' />
              </div>
              <p>{t('onboarding.method.4')}</p>
            </div>
          </div>
        </div>
        <div className='flex w-fit flex-col items-center justify-center gap-2 text-center'>
          <img src={qrCodeURL} alt='qr-code' />
          <div>
            <Trans i18nKey='onboarding.desktop.qrCode.title' components={{ h5: <h5 /> }} />
          </div>
          <div>
            <Trans
              i18nKey='onboarding.desktop.qrCode.description'
              components={{ p: <p className='!text-text-secondary text-body2' /> }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function OnBoardingMobile() {
  const { t } = useTranslation();
  const { setStep } = useEkyc();

  const handleClick = () => {
    setStep('terms-and-conditions');
  };

  return (
    <div className='flex h-dvh flex-col'>
      <div className='flex h-full w-full flex-col items-center justify-center'>
        <div className='flex w-full justify-between gap-6 rounded-2xl'>
          <div className='mx-auto flex w-full max-w-md flex-col items-center gap-8 p-4'>
            <img src='/logo.svg' alt='logo' className='h-10 self-start' />
            <div className='flex w-full flex-col gap-2 text-left'>
              <h3>{t('onboarding.mobile.title')}</h3>
              <div className='text-text-secondary'>
                <Trans i18nKey='onboarding.mobile.description' />
              </div>
            </div>
            <div className='text-body2 flex w-full flex-col gap-3'>
              <div className='flex gap-3'>
                <IDCardIcon className='text-primary' />
                <p>{t('onboarding.method.2')}</p>
              </div>
              <div className='flex gap-3'>
                <EventAvailableOutlinedIcon className='text-primary' />
                <p>{t('onboarding.method.3')}</p>
              </div>
              <div className='flex max-w-[300px] items-center gap-3'>
                <div>
                  <ArOnYouIcon className='text-primary' />
                </div>
                <p>{t('onboarding.method.4')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='mx-auto h-fit w-full max-w-md self-center px-4 pt-4 pb-8'>
        <Button fullWidth onClick={handleClick}>
          {t('onboarding.button')}
        </Button>
      </div>
    </div>
  );
}
